import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getTasks, getSeriesHistory, createTask, updateTask, deleteTask, batchUpdateTasks, batchDeleteTasks, setSubtaskDone, setSeriesActive, createTaskCategory,
} from "../../services/taskService";
import { getCustomFields } from "../../services/customFieldService";
import { useCurrentUser } from "../../context/UserContext";
import { useTaskOverdue } from "../../context/TaskOverdueContext";
import { usePermissions } from "../../hooks/usePermission";
import { useCurrency } from "../../hooks/useCurrency";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import useTableColumns from "../../hooks/useTableColumns";
import useSelection from "../../hooks/useSelection";
import useBatchRunner from "../../hooks/useBatchRunner";
import Toast from "../../components/Toast";
import ConfirmDialog from "../../components/ConfirmDialog";
import FormModal from "../../components/forms/FormModal";
import DataTable from "../../components/forms/DataTable";
import CustomDropdown from "../../components/forms/CustomDropdown";
import KanbanView from "../../components/projects/KanbanView";
import SegmentedControl from "../../components/common/SegmentedControl";
import RowActionsMenu from "../../components/common/RowActionsMenu";
import BatchBar from "../../components/common/BatchBar";
import Avatar from "../../components/common/Avatar";
import CustomFieldRows from "../../components/common/CustomFieldRows";
import ExportDialog from "../../components/common/ExportDialog";
import CustomFieldModal from "../../components/settings/CustomFieldModal";
import chrome from "../../components/common/PageChrome.module.css";
import ui from "./DashboardUI.module.css";
import styles from "./tasks/TasksUI.module.css";
import {
  DEFAULT_FILTERS, GROUPS, GROUP_ORDER, PRIORITIES, PRIORITY_STYLE, TASK_COLUMNS, TASK_GROUPINGS, dueInfo, dueRuleText,
  exportColumns, filterTasks, normalizeTask, ordinal, previewStarts, repeatShort, scheduleText, shortDate, sortTasks, subtaskProgress,
} from "./tasks/tasksConfig";
import { errorMessage } from "../../utils/errorMessage";
import { toggleSort } from "../../utils/sortRows";
import { addDays, dayOf, toISODate } from "../../utils/weekDates";
import { customCellRenderers } from "../../utils/customFieldUtils";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const NO_FIELDS = [];
const NEW_CATEGORY = "__new";
const DAY_OPTIONS = [
  ...Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: `${ordinal(i + 1)} of the month` })),
  { value: "last", label: "Last day of the month" },
];
const BOARD_COLUMNS = [
  { id: "upcoming", name: "Upcoming", color: GROUPS.upcoming.dot },
  { id: "todo", name: "To do", color: GROUPS.todo.dot },
  { id: "progress", name: "In progress", color: GROUPS.progress.dot },
  { id: "done", name: "Completed", color: GROUPS.done.dot },
];
const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const DEFAULT_DISPLAY = { upcoming: true, density: "comfortable" };
const remembered = (key, fallback) => {
  try { return { ...fallback, ...JSON.parse(localStorage.getItem(key)) }; } catch { return fallback; }
};

const blankForm = (assignee, category, today) => ({
  id: null, series_id: null, title: "", description: "", assignee: String(assignee || ""), category, newCategory: "", priority: "medium",
  due_on: addDays(today, 7), repeat: false, day: "25", time: "09:00", offset: "0", subtasks: [], newSubtask: "", custom_fields: {}, anchor: null,
});

export default function Tasks() {
  const currentUser = useCurrentUser();
  const { canManageTasks, canManageCompanySettings } = usePermissions();
  const { format: formatMoney } = useCurrency();
  const { refreshOverdueCount } = useTaskOverdue();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = toISODate(new Date());
  const selfId = currentUser?.employee_id;

  const [data, setData] = useState({ tasks: [], series: [], people: [], categories: [] });
  const [fields, setFields] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const showLoading = useMinimumLoader(!loaded);
  const [toast, setToast] = useState(null);
  const runBatch = useBatchRunner(setToast);
  const showToast = (message, type = "success") => setToast({ message, type });

  const [view, setViewState] = useState(() => {
    try { return localStorage.getItem("tasksView") || "list"; } catch { return "list"; }
  });
  const setView = (v) => {
    setViewState(v);
    try { localStorage.setItem("tasksView", v); } catch { /* remembering the view is optional */ }
  };
  const [display, setDisplayState] = useState(() => remembered("tasksDisplay", DEFAULT_DISPLAY));
  const setDisplay = (patch) => {
    const next = { ...display, ...patch };
    setDisplayState(next);
    try { localStorage.setItem("tasksDisplay", JSON.stringify(next)); } catch { /* remembering display options is optional */ }
  };
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [collapsed, setCollapsed] = useState({ [GROUPS.done.label]: true });
  const [openSubs, setOpenSubs] = useState({});
  const [form, setForm] = useState(null);
  const [formTab, setFormTab] = useState("details");
  const [formError, setFormError] = useState(null);
  const [history, setHistory] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [dueForm, setDueForm] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [addDetail, setAddDetail] = useState(false);

  const customFields = loaded ? fields : NO_FIELDS;
  const columnState = useTableColumns("taskColumns", TASK_COLUMNS, customFields);
  const selection = useSelection();

  const load = useCallback(async () => {
    const [res, cf] = await Promise.all([getTasks(toISODate(new Date())), getCustomFields("task").catch(() => ({ fields: [] }))]);
    setData(res);
    setFields(cf.fields || []);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    load()
      .catch((err) => setToast({ message: errorMessage(err, "Failed to load tasks."), type: "error" }))
      .finally(() => setLoaded(true));
  }, [currentUser, load]);

  const refresh = useCallback(async () => {
    await load();
    refreshOverdueCount();
  }, [load, refreshOverdueCount]);

  const seriesById = useMemo(() => Object.fromEntries(data.series.map((s) => [s.id, s])), [data.series]);
  const tasks = useMemo(
    () => data.tasks.map((t) => ({ ...normalizeTask(t, today), series: seriesById[t.series_id] || null })),
    [data.tasks, seriesById, today]
  );
  const openCount = tasks.filter((t) => t.group !== "done" && t.group !== "upcoming").length;

  const f = filters;
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const filtered = filterTasks(display.upcoming ? tasks : tasks.filter((t) => t.group !== "upcoming"), f);
  // Grouping by anything but status lists open work only, so Completed doesn't swamp the groups.
  const rows = sortTasks(f.groupBy === "status" ? filtered : filtered.filter((t) => t.group !== "done"), f.sort, customFields);
  const grouping = TASK_GROUPINGS[f.groupBy] || null;
  const selectedRows = rows.filter((t) => selection.selectedIds.has(t.id));

  // ---- Status changes and batch edits (each offers Undo) ----
  const undoBatch = (res) => (res.updated.length ? batchUpdateTasks({ restore: res.previous, created: res.created.map((c) => c.id) }) : null);
  const change = (ids, patch, describe) => runBatch(() => batchUpdateTasks({ ids, patch }), refresh, describe, undoBatch);

  const completeMessage = (res, title) => {
    if (!res.updated.length) return "Nothing to change.";
    const next = res.created[0];
    if (res.updated.length > 1) return `${plural(res.updated.length, "task", "tasks")} completed.${res.created.length ? " Recurring ones created their next task." : ""}`;
    return next ? `Archived. Next one starts ${shortDate(next.starts_on)}, ${next.start_time}.` : `“${title}” completed.`;
  };
  const setStatus = (task, status) => change([task.id], { status }, (res) => (
    status === "done" ? completeMessage(res, task.title) : res.updated.length ? `Moved to ${status === "todo" ? "To do" : "In progress"}.` : "Nothing to change."
  ));

  const batch = (patch, label) => {
    const ids = selectedRows.map((t) => t.id);
    const title = selectedRows[0]?.title;
    selection.clear();
    change(ids, patch, (res) => (patch.status === "done" ? completeMessage(res, title) : `${plural(res.updated.length, "task", "tasks")} ${label}.`));
  };

  const confirmDelete = (ids, message) => setConfirm({
    message,
    confirmText: "Delete",
    onConfirm: async () => {
      setConfirm(null);
      try {
        const res = ids.length === 1 ? await deleteTask(ids[0]) : await batchDeleteTasks(ids);
        selection.clear();
        setForm(null);
        await refresh();
        showToast(`${plural(res.deleted, "task", "tasks")} deleted.`);
      } catch (err) {
        showToast(errorMessage(err, "Failed to delete."), "error");
      }
    },
  });

  // Ticks at once and puts the box back if the save fails.
  const toggleSubtask = async (task, sub) => {
    const put = (subtasks) => setData((prev) => ({ ...prev, tasks: prev.tasks.map((t) => (t.id === task.id ? { ...t, subtasks } : t)) }));
    const subtasks = task.subtasks.map((s) => (s.id === sub.id ? { ...s, done: !s.done } : s));
    put(subtasks);
    try {
      await setSubtaskDone(task.id, sub.id, !sub.done);
      if (subtasks.every((s) => s.done) && task.status !== "done") showToast("All subtasks done. Tick the task to complete it.");
    } catch (err) {
      put(task.subtasks);
      showToast(errorMessage(err, "Failed to update the subtask."), "error");
    }
  };

  const toggleSeries = async (series, active) => {
    const put = (on) => setData((prev) => ({ ...prev, series: prev.series.map((s) => (s.id === series.id ? { ...s, active: on } : s)) }));
    put(active);
    try {
      await setSeriesActive(series.id, active, today);
      await refresh();
      showToast(active ? `“${series.title}” resumed.` : `“${series.title}” paused. Completing the open task won't create a new one.`);
    } catch (err) {
      put(!active);
      showToast(errorMessage(err, "Failed to update the series."), "error");
    }
  };

  // ---- Task form ----
  const categories = data.categories.map((c) => c.name);
  const categoryColor = Object.fromEntries(data.categories.map((c) => [c.name, c.color]));
  const openNew = () => {
    setFormTab("details");
    setFormError(null);
    setForm(blankForm(selfId, categories[0] || "", today));
  };
  const openTask = useCallback((t) => {
    setFormTab("details");
    setFormError(null);
    setHistory(null);
    const s = t.series;
    setForm({
      id: t.id, series_id: t.series_id, title: t.title, description: t.description || "", assignee: String(t.assignee_employee_id || ""),
      category: t.category, newCategory: "", priority: t.priority, due_on: t.due_on, repeat: Boolean(s),
      day: s ? s.day_of_month : "25", time: s ? s.start_time : "09:00", offset: s ? String(s.due_offset_days) : "0",
      subtasks: t.subtasks.map((x) => ({ ...x })), newSubtask: "", custom_fields: { ...t.custom_fields }, anchor: t.starts_on,
    });
  }, []);
  const closeForm = () => { setForm(null); setHistory(null); };
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Other pages link here with ?open=<task title>.
  useEffect(() => {
    const title = searchParams.get("open");
    if (!loaded || !title) return;
    const match = tasks.find((t) => t.title === title && t.status !== "done") || tasks.find((t) => t.title === title);
    if (match) openTask(match);
    else setToast({ message: `No task called “${title}”.`, type: "error" });
    setSearchParams({}, { replace: true });
  }, [loaded, searchParams, tasks, openTask, setSearchParams]);

  const loadHistory = async (tab) => {
    setFormTab(tab);
    if (tab !== "history" || history) return;
    try {
      setHistory(await getSeriesHistory(form.series_id));
    } catch (err) {
      showToast(errorMessage(err, "Failed to load history."), "error");
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setFormError("Task name is required.");
    const isNew = form.category === NEW_CATEGORY;
    const category = isNew ? form.newCategory.trim() : form.category;
    if (!category) return setFormError("Give the new category a name.");
    if (isNew && !categories.some((c) => c.toLowerCase() === category.toLowerCase())) {
      try {
        await createTaskCategory({ name: category });
      } catch (err) {
        return setFormError(errorMessage(err, "Failed to add the category."));
      }
    }
    const body = {
      title: form.title, description: form.description, category, priority: form.priority,
      assignee_employee_id: form.assignee ? Number(form.assignee) : null,
      due_on: form.due_on, subtasks: form.subtasks.map(({ text, done }) => ({ text, done })), custom_fields: form.custom_fields,
      repeat: form.repeat ? { day_of_month: form.day, start_time: form.time, due_offset_days: Number(form.offset) || 0 } : null,
      today,
    };
    try {
      const saved = form.id ? await updateTask(form.id, body) : await createTask(body);
      const message = !form.repeat
        ? (form.series_id ? "Stopped repeating. This task is now a one-off." : form.id ? "Task saved." : "Task created.")
        : form.series_id ? "Series updated. Open tasks follow the new schedule."
          : `${form.id ? "Now repeats" : "Recurring task created"}. First one starts ${shortDate(saved.starts_on)}, ${saved.start_time}.`;
      closeForm();
      await refresh();
      showToast(message);
    } catch (err) {
      setFormError(errorMessage(err, "Failed to save the task."));
    }
  };

  const addSubtask = () => {
    const text = form.newSubtask.trim();
    if (!text) return;
    set({ subtasks: [...form.subtasks, { id: `new-${Date.now()}`, text, done: false }], newSubtask: "" });
  };
  const editSub = (id, patch) => set({ subtasks: form.subtasks.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

  const peopleOptions = data.people.map((p) => ({ value: String(p.id), label: p.name }));
  const categoryOptions = [...categories.map((c) => ({ value: c, label: c })), ...(canManageTasks ? [{ value: NEW_CATEGORY, label: "+ New category" }] : [])];
  const priorityOptions = PRIORITIES.map((p) => ({ value: p, label: capital(p) }));

  const renderSubtasks = () => {
    const done = form.subtasks.filter((s) => s.done).length;
    return (
      <div className={styles.subtasks}>
        {form.subtasks.length > 0 && (
          <div className={styles.subProgress}>
            <span className={styles.subTrack}><span style={{ width: `${(done / form.subtasks.length) * 100}%` }} /></span>
            {done} of {form.subtasks.length} done
          </div>
        )}
        {form.subtasks.map((s) => (
          <div key={s.id} className={styles.subRow}>
            <input type="checkbox" checked={s.done} aria-label={`Done: ${s.text}`} onChange={() => editSub(s.id, { done: !s.done })} />
            <input type="text" value={s.text} aria-label="Subtask" className={s.done ? styles.subDone : ""} onChange={(e) => editSub(s.id, { text: e.target.value })} />
            <button type="button" className={styles.subRemove} aria-label={`Remove ${s.text}`} onClick={() => set({ subtasks: form.subtasks.filter((x) => x.id !== s.id) })}>✕</button>
          </div>
        ))}
        <div className={styles.subRow}>
          <input
            type="text"
            placeholder="Add a subtask"
            aria-label="New subtask"
            value={form.newSubtask}
            onChange={(e) => set({ newSubtask: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubtask(); } }}
          />
          <button type="button" className={ui.toolbarReset} onClick={addSubtask} disabled={!form.newSubtask.trim()}>Add</button>
        </div>
      </div>
    );
  };

  const renderRepeat = () => (
    <div className={styles.repeat}>
      <label className={styles.repeatToggle}>
        <input type="checkbox" checked={form.repeat} onChange={(e) => set({ repeat: e.target.checked })} />
        Repeats monthly
      </label>
      {form.repeat && (
        <>
          <div className={styles.repeatGrid}>
            {/* Not a <label>: a label re-clicks the dropdown's button, closing the menu as it opens. */}
            <div>Day<CustomDropdown fullWidth options={DAY_OPTIONS} value={form.day} onChange={(v) => set({ day: v })} /></div>
            <label>Start time<input type="time" value={form.time} onChange={(e) => set({ time: e.target.value })} /></label>
            <label>Due after (days)<input type="number" min="0" max="60" value={form.offset} onChange={(e) => set({ offset: e.target.value })} /></label>
          </div>
          <p className={styles.preview}>
            Next: {previewStarts(form.day, form.series_id && form.anchor ? form.anchor : today)
              .map((d) => `${shortDate(d)} (due ${shortDate(addDays(d, Number(form.offset) || 0))})`).join(" · ")}
          </p>
        </>
      )}
    </div>
  );

  const formFields = form ? [
    { label: "Task name", value: form.title, onChange: (e) => { set({ title: e.target.value }); setFormError(null); } },
    { label: "Description", type: "textarea", value: form.description, onChange: (e) => set({ description: e.target.value }) },
    {
      type: "row",
      fields: [
        { label: "Assignee", type: "select", value: form.assignee, options: peopleOptions, disabled: !canManageTasks, onChange: (e) => set({ assignee: e.target.value }) },
        { label: "Priority", type: "select", value: form.priority, options: priorityOptions, onChange: (e) => set({ priority: e.target.value }) },
      ],
    },
    {
      type: "row",
      fields: [
        { label: "Category", type: "select", value: form.category, options: categoryOptions, onChange: (e) => set({ category: e.target.value }) },
        ...(form.repeat ? [] : [{ label: "Due date", type: "date", value: form.due_on, onChange: (e) => set({ due_on: e.target.value }) }]),
      ],
    },
    ...(form.category === NEW_CATEGORY ? [{ label: "New category", value: form.newCategory, placeholder: "e.g. Facilities", onChange: (e) => set({ newCategory: e.target.value }) }] : []),
    { type: "custom", render: renderRepeat },
    { label: "Subtasks", type: "custom", render: renderSubtasks },
    ...(customFields.length ? [{
      type: "custom",
      render: () => (
        <CustomFieldRows
          fields={customFields}
          entity={form}
          formatMoney={formatMoney}
          onSave={(field, value) => set({ custom_fields: { ...form.custom_fields, [field.field_id]: value } })}
        />
      ),
    }] : []),
  ] : [];

  const historyContent = formTab === "history" ? (
    history ? (
      <ul className={styles.history}>
        {history.map((t) => (
          <li key={t.id}>
            <span>
              <strong>{t.starts_on ? new Date(`${t.starts_on}T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : shortDate(t.due_on)}</strong>
              <small>
                {t.status === "done"
                  ? `Completed ${t.done_at ? shortDate(dayOf(t.done_at)) : ""}${t.done_by_name ? ` by ${t.done_by_name}` : ""}`
                  : `Starts ${shortDate(t.starts_on)} · due ${shortDate(t.due_on)}`}
              </small>
            </span>
            <span className={`${styles.historyPill} ${styles[`history_${t.status === "done" ? "done" : t.starts_on > today ? "upcoming" : "open"}`]}`}>
              {t.status === "done" ? "Archived" : t.starts_on > today ? "Upcoming" : "Open"}
            </span>
          </li>
        ))}
      </ul>
    ) : <p className={styles.preview}>Loading…</p>
  ) : null;

  // ---- Cells ----
  const categoryPill = (name) => (
    <span className={styles.category}><span className={styles.categoryDot} style={{ background: categoryColor[name] || "#9ca3af" }} />{name}</span>
  );
  const priorityPill = (p) => (
    <span className={styles.pill} style={{ "--pill-bg": PRIORITY_STYLE[p].bg, "--pill-fg": PRIORITY_STYLE[p].fg }}>{p}</span>
  );
  const personCell = (t) => (t.assignee_name ? (
    <span className={chrome.nameCell}><Avatar name={t.assignee_name} imageUrl={t.assignee_avatar} size="sm" round /><span>{t.assignee_name}</span></span>
  ) : "—");
  const dueCell = (t) => {
    const d = dueInfo(t, today);
    return <span className={styles[`due_${d.tone}`]}>{d.text}{d.sub && <span className={chrome.cellSub}>{d.sub}</span>}</span>;
  };

  const titleCell = (t) => {
    const done = t.status === "done";
    const open = openSubs[t.id];
    return (
      <div className={styles.titleCell}>
        <button
          type="button"
          className={`${styles.tick} ${done ? styles.ticked : ""}`}
          aria-label={done ? `Reopen ${t.title}` : `Complete ${t.title}`}
          title={done ? "Reopen task" : "Mark complete"}
          onClick={(e) => { e.stopPropagation(); setStatus(t, done ? "todo" : "done"); }}
        >✓</button>
        <div className={styles.titleText}>
          <span className={done ? styles.doneTitle : ""}>{t.title}</span>
          {(t.series || t.subtasks.length > 0) && (
            <span className={styles.meta}>
              {t.series && <span className={styles.repeatPill} title="Repeats monthly">↻ {repeatShort(t.series)}</span>}
              {t.subtasks.length > 0 && (
                <button
                  type="button"
                  className={`${styles.subPill} ${t.subtasks.every((s) => s.done) ? styles.subAllDone : ""}`}
                  aria-expanded={Boolean(open)}
                  aria-label={`Subtasks ${subtaskProgress(t.subtasks)}`}
                  onClick={(e) => { e.stopPropagation(); setOpenSubs((prev) => ({ ...prev, [t.id]: !prev[t.id] })); }}
                >{open ? "▾" : "▸"} ☑ {subtaskProgress(t.subtasks)}</button>
              )}
            </span>
          )}
          {open && (
            <span className={styles.inlineSubs} onClick={(e) => e.stopPropagation()}>
              {t.subtasks.map((s) => (
                <label key={s.id} className={s.done ? styles.subDone : ""}>
                  <input type="checkbox" checked={s.done} onChange={() => toggleSubtask(t, s)} />{s.text}
                </label>
              ))}
            </span>
          )}
        </div>
      </div>
    );
  };

  const cells = {
    title: titleCell,
    category: (t) => categoryPill(t.category),
    assignee: personCell,
    starts: (t) => (t.starts_on ? <span>{shortDate(t.starts_on)}<span className={chrome.cellSub}>{t.start_time}</span></span> : "—"),
    due: dueCell,
    priority: (t) => priorityPill(t.priority),
    repeat: (t) => (t.series ? repeatShort(t.series) : "—"),
    description: (t) => t.description || "—",
    completed: (t) => (t.done_at ? shortDate(dayOf(t.done_at)) : "—"),
    ...customCellRenderers(customFields, formatMoney),
  };

  // ---- Board ----
  const boardColumnOf = (t) => (t.group === "upcoming" ? "upcoming" : t.status);
  const moveCard = (t, to) => {
    if (to === "upcoming") return showToast("Upcoming tasks start on their own date.", "error");
    if (t.group === "upcoming" && to !== "done") return showToast(`This task starts ${shortDate(t.starts_on)}.`, "error");
    return setStatus(t, to);
  };
  const renderCard = (t) => {
    const d = dueInfo(t, today);
    return (
      <div className={styles.card}>
        <span className={styles.cardTitle}>{t.title}</span>
        <span className={styles.meta}>
          {categoryPill(t.category)}
          {priorityPill(t.priority)}
          {t.series && <span className={styles.repeatPill}>↻ {repeatShort(t.series)}</span>}
        </span>
        <span className={styles.cardFoot}>
          <span>{t.assignee_name || "Unassigned"}</span>
          <span className={styles[`due_${d.tone}`]}>
            {t.group === "done" ? `Done ${d.sub.replace("on ", "")}` : t.group === "upcoming" ? `Starts ${shortDate(t.starts_on)}` : d.sub.includes("late") ? d.sub : `Due ${d.text}`}
          </span>
        </span>
      </div>
    );
  };

  // ---- Recurring ----
  const seriesRows = data.series.filter((s) => (!f.person || String(s.assignee_employee_id) === f.person)
    && (!f.category || s.category === f.category) && (!f.priority || s.priority === f.priority)
    && (!f.search.trim() || s.title.toLowerCase().includes(f.search.trim().toLowerCase())));
  const openSeries = (s) => {
    const current = tasks.filter((t) => t.series_id === s.id && t.status !== "done").sort((a, b) => a.starts_on.localeCompare(b.starts_on))[0];
    if (current) openTask(current);
    else showToast("This series has no open task. Resume it to create the next one.", "error");
  };
  const seriesCells = {
    title: (s) => <span className={s.active ? "" : styles.paused}>{s.title}</span>,
    assignee: (s) => s.assignee_name || "—",
    schedule: (s) => <span>{scheduleText(s)}<span className={chrome.cellSub}>{dueRuleText(s.due_offset_days)}</span></span>,
    next: (s) => (s.active && s.next_starts_on ? shortDate(s.next_starts_on) : "Paused"),
    history: (s) => (s.done_count ? plural(s.done_count, "task", "tasks") : "—"),
    active: (s) => (
      <label className={styles.switch} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={s.active} aria-label={`${s.active ? "Pause" : "Resume"} ${s.title}`} onChange={(e) => toggleSeries(s, e.target.checked)} />
        {s.active ? "Active" : "Paused"}
      </label>
    ),
  };
  const SERIES_COLUMNS = [
    { key: "title", label: "Task", visible: true }, { key: "assignee", label: "Assignee", visible: true },
    { key: "schedule", label: "Schedule", visible: true }, { key: "next", label: "Next start", visible: true },
    { key: "history", label: "Completed", visible: true }, { key: "active", label: "Status", visible: true },
  ];

  if (showLoading) {
    return (
      <div className={ui.pageContainer}>
        <div className={ui.toolbarSkeleton}>
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className={`${ui.skeletonBlock} ${ui.toolbarSkeletonPill}`} />)}
        </div>
        <div className={ui.tableSkeleton}>
          <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />)}
        </div>
      </div>
    );
  }

  const fieldsForExport = exportColumns(columnState.columns, customFields);
  const groupFilterOptions = [{ value: "", label: "No Grouping" }, ...Object.entries(TASK_GROUPINGS).map(([value, g]) => ({ value, label: `Group by ${g.label}` }))];
  const editingSeries = Boolean(form?.series_id);

  return (
    <div className={ui.fadeInContent}>
      {toast && <Toast message={toast.message} type={toast.type} action={toast.action} onClose={() => setToast(null)} />}

      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft} style={{ alignItems: "center" }}>
          <h1 className={ui.pageTitle}>Tasks</h1>
          <span className={styles.summary}>{openCount} open</span>
        </div>
        <div className={ui.pageHeaderRight}>
          <SegmentedControl
            ariaLabel="List, board or recurring"
            options={[{ value: "list", label: "List" }, { value: "board", label: "Board" }, { value: "recurring", label: "Recurring" }]}
            value={view}
            onChange={setView}
          />
          <RowActionsMenu actions={[
            { label: "Export…", onClick: () => setExporting("filtered") },
            { label: display.upcoming ? "Hide upcoming tasks" : "Show upcoming tasks", onClick: () => setDisplay({ upcoming: !display.upcoming }) },
            { label: display.density === "compact" ? "Comfortable rows" : "Compact rows", onClick: () => setDisplay({ density: display.density === "compact" ? "comfortable" : "compact" }) },
          ]} />
          <button type="button" className={ui.addButton} onClick={openNew}>
            <span className={ui.icon}>＋</span> Add Task
          </button>
        </div>
      </div>

      <div className={ui.tableToolbar}>
        <input
          type="text"
          className={ui.toolbarSearch}
          placeholder="Search tasks…"
          aria-label="Search tasks"
          value={f.search}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
        {canManageTasks && <CustomDropdown toolbar options={[{ value: "", label: "All People" }, ...peopleOptions]} value={f.person} onChange={(v) => setFilter({ person: v })} />}
        <CustomDropdown toolbar options={[{ value: "", label: "All Categories" }, ...categories.map((c) => ({ value: c, label: c }))]} value={f.category} onChange={(v) => setFilter({ category: v })} />
        <CustomDropdown toolbar options={[{ value: "", label: "All Priorities" }, ...priorityOptions]} value={f.priority} onChange={(v) => setFilter({ priority: v })} />
        {view === "list" && <CustomDropdown toolbar options={groupFilterOptions} value={f.groupBy} onChange={(v) => setFilter({ groupBy: v })} />}
        <button type="button" className={ui.toolbarReset} onClick={() => setFilters(DEFAULT_FILTERS)}>Clear</button>
      </div>

      {view === "list" && (
        <BatchBar count={selection.selectedIds.size} onClear={selection.clear}>
          <button type="button" onClick={() => batch({ status: "done" })}>Mark complete</button>
          <CustomDropdown toolbar value="" options={[{ value: "", label: "Change status…" }, { value: "todo", label: "To do" }, { value: "progress", label: "In progress" }]} onChange={(v) => v && batch({ status: v }, `moved to ${v === "todo" ? "To do" : "In progress"}`)} />
          {canManageTasks && <CustomDropdown toolbar value="" options={[{ value: "", label: "Assign to…" }, ...peopleOptions]} onChange={(v) => v && batch({ assignee_employee_id: Number(v) }, `assigned to ${peopleOptions.find((p) => p.value === v)?.label}`)} />}
          <CustomDropdown toolbar value="" options={[{ value: "", label: "Category…" }, ...categories.map((c) => ({ value: c, label: c }))]} onChange={(v) => v && batch({ category: v }, `moved to ${v}`)} />
          <CustomDropdown toolbar value="" options={[{ value: "", label: "Priority…" }, ...priorityOptions]} onChange={(v) => v && batch({ priority: v }, `set to ${v} priority`)} />
          <button type="button" onClick={() => setDueForm({ due_on: addDays(today, 7) })}>Set due date…</button>
          <button type="button" onClick={() => setExporting("selected")}>Export</button>
          <button type="button" onClick={() => confirmDelete(selectedRows.map((t) => t.id), `Delete ${plural(selectedRows.length, "task", "tasks")}? Recurring ones stop repeating; completed history is kept.`)}>Delete</button>
        </BatchBar>
      )}

      {view === "board" && (
        <div className={styles.boardWrap}>
          <KanbanView
            columns={display.upcoming ? BOARD_COLUMNS : BOARD_COLUMNS.filter((c) => c.id !== "upcoming")}
            density={display.density}
            items={filtered}
            getColumnId={boardColumnOf}
            renderCard={renderCard}
            onCardMove={moveCard}
            onCardClick={openTask}
            canEdit
            getEmptyLabel={(col, over) => (over ? (col.id === "done" ? "Drop here to complete" : "Drop here") : col.id === "upcoming" ? "No upcoming recurring tasks" : "Nothing here")}
          />
        </div>
      )}

      {view === "recurring" && (seriesRows.length === 0 ? (
        <p className={styles.empty}>{data.series.length ? "No recurring tasks match these filters." : "No recurring tasks yet. Tick “Repeats monthly” on a task to make one."}</p>
      ) : (
        <DataTable
          columns={SERIES_COLUMNS}
          data={seriesRows}
          getRowId={(s) => s.id}
          onRowClick={openSeries}
          customCellRender={seriesCells}
        />
      ))}

      {view === "list" && (rows.length === 0 ? (
        <p className={styles.empty}>{tasks.length === 0 ? "No tasks yet. Add one to get started." : "No tasks match these filters."}</p>
      ) : (
        <div className={display.density === "compact" ? styles.compact : undefined}>
          <DataTable
            columns={columnState.columns}
            data={rows}
            storageKey="taskColumns"
            groupedBy={grouping ? grouping.get : null}
            groupOrder={f.groupBy === "status" ? GROUP_ORDER : f.groupBy === "priority" ? PRIORITIES.map(capital) : []}
            getGroupColor={f.groupBy === "status" ? (name) => Object.values(GROUPS).find((g) => g.label === name)?.dot : null}
            collapsedGroups={collapsed}
            toggleGroupCollapse={(g) => setCollapsed((prev) => ({ ...prev, [g]: !prev[g] }))}
            renderGroupExtra={f.groupBy === "status" ? (grouped) => (
              grouped[0].group === "upcoming" ? <span className={styles.groupNote}>Recurring tasks that haven't started yet</span>
                : grouped[0].group === "done" ? <span className={styles.groupNote}>Archived in the last 30 days</span> : null
            ) : null}
            sortColumn={f.sort?.key}
            sortDirection={f.sort?.dir || "asc"}
            onSort={(key) => setFilter({ sort: toggleSort(f.sort, key) })}
            onRowClick={openTask}
            onToggleColumn={columnState.toggleColumn}
            onMoveColumn={columnState.moveColumn}
            onAddDetail={canManageCompanySettings ? () => setAddDetail(true) : null}
            selectable
            selectedIds={selection.selectedIds}
            getRowId={(t) => t.id}
            onToggleRow={selection.toggleRow}
            onToggleGroup={selection.toggleGroup}
            onToggleAll={(checked) => selection.toggleAll(rows.map((t) => t.id), checked)}
            customCellRender={cells}
          />
        </div>
      ))}

      {addDetail && (
        <CustomFieldModal
          defaultEntity="task"
          onClose={() => setAddDetail(false)}
          onSaved={() => load().catch((err) => showToast(errorMessage(err, "Failed to reload."), "error"))}
          onMessage={setToast}
        />
      )}

      {form && (
        <FormModal
          title={!form.id ? "New Task" : editingSeries ? "Recurring Task" : "Edit Task"}
          onCancel={closeForm}
          onSubmit={formTab === "details" ? submitForm : null}
          error={formError}
          submitLabel={form.id ? "Save" : "Add task"}
          tabs={editingSeries ? [{ key: "details", label: "Details" }, { key: "history", label: "History" }] : null}
          activeTab={editingSeries ? formTab : null}
          onTabChange={loadHistory}
          customTabContent={historyContent}
          footerActions={form.id ? (
            <button
              type="button"
              className={ui.buttonDanger}
              onClick={() => confirmDelete([form.id], editingSeries
                ? `Delete “${form.title}” and stop it repeating? Completed history is kept.`
                : `Delete “${form.title}”?`)}
            >{editingSeries ? "Delete series" : "Delete task"}</button>
          ) : null}
          fields={formFields}
        />
      )}

      {dueForm && (
        <FormModal
          title="Set Due Date"
          onCancel={() => setDueForm(null)}
          onSubmit={(e) => { e.preventDefault(); const { due_on } = dueForm; setDueForm(null); if (due_on) batch({ due_on }, `due ${shortDate(due_on)}`); }}
          submitLabel="Apply"
          fields={[
            { label: "Due date", type: "date", value: dueForm.due_on, onChange: (e) => setDueForm({ due_on: e.target.value }) },
            { type: "custom", render: () => <p className={styles.preview}>Applies to the {plural(selectedRows.length, "task", "tasks")} ticked; completed ones keep their date.</p> },
          ]}
        />
      )}

      {exporting && (
        <ExportDialog
          title="Export tasks"
          filename="tasks"
          scopes={[
            { value: "filtered", label: "Rows shown", rows },
            ...(selectedRows.length ? [{ value: "selected", label: "Ticked rows", rows: selectedRows }] : []),
            { value: "all", label: "All loaded tasks", rows: tasks },
          ]}
          defaultScope={exporting}
          columns={fieldsForExport}
          groups={Object.entries(TASK_GROUPINGS).map(([value, g]) => ({ value, label: g.label, get: g.get }))}
          defaultGroup={f.groupBy}
          onExported={(count, kind) => showToast(`${plural(count, "row", "rows")} exported${kind === "csv" ? " as CSV" : ""}.`)}
          onClose={() => setExporting(null)}
        />
      )}

      {confirm && <ConfirmDialog message={confirm.message} confirmText={confirm.confirmText} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}
